import React, { useState, useEffect } from 'react';
import { BookOpenIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { PriestReflection } from '../types';
import { apiService } from '../services/api';

const Reflections: React.FC = () => {
  const [reflections, setReflections] = useState<PriestReflection[]>([]);
  const [selectedReflection, setSelectedReflection] = useState<PriestReflection | null>(null);

  useEffect(() => {
    const fetchReflections = async () => {
      try {
        const reflectionsData = await apiService.getReflections();
        // Sort reflections by date in descending order (most recent first)
        const sortedReflections = [...reflectionsData].sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateB.getTime() - dateA.getTime();
        });
        setReflections(sortedReflections);
        
        // Check for hash in URL to select specific reflection
        const hash = window.location.hash.substring(1);
        const targetReflection = hash ? sortedReflections.find(r => r.id === hash) : null;
        
        if (targetReflection) {
          setSelectedReflection(targetReflection);
        } else if (sortedReflections.length > 0) {
          setSelectedReflection(sortedReflections[0]);
        }
      } catch (error) {
        console.error('Error fetching reflections:', error);
        // Fallback to production reflections data
        const productionReflections: PriestReflection[] = [
      {
        id: '1',
        title: 'Finding Light in the Darkness',
        content: `Dear beloved parishioners,

As we journey through this Advent season, I am reminded of the profound truth that even in our darkest moments, Christ's light shines brightest. The waiting and anticipation of Advent teaches us patience, hope, and trust in God's timing.

In our modern world, filled with uncertainties and challenges, we are called to be beacons of that same light for others. Each act of kindness, each moment of compassion, each prayer offered in love becomes a reflection of Christ's presence among us.

I encourage you all to take time this week for quiet reflection. In the stillness, we often find the answers we seek and the peace our hearts desire. Remember that our church family is here to support one another, especially during difficult times.

The season of Advent reminds us that God's love never fails, even when we cannot see the path ahead clearly. Just as Mary said "yes" to God's plan despite not understanding it fully, we too are called to trust in divine providence.

May this season of preparation fill your hearts with hope and your homes with peace. I look forward to celebrating the joy of Christ's birth with all of you.

In faith and service,
Father Rejimon Devasia`,
        date: '2024-11-24',
        excerpt: 'As we journey through this Advent season, I am reminded of the profound truth that even in our darkest moments, Christ\'s light shines brightest...'
      },
      {
        id: '2',
        title: 'Gratitude in All Things',
        content: `My dear friends in Christ,

This week, as we celebrated Thanksgiving, I was overwhelmed by the many blessings that surround our parish community. From the volunteers who serve tirelessly in our ministries to the families who open their hearts and homes to those in need, I see Christ's love in action every day.

Gratitude is not just a feeling we experience during Thanksgiving; it is a way of life that transforms how we see the world. When we live with grateful hearts, we become more aware of God's presence in the ordinary moments of our lives.

St. Paul reminds us to "give thanks in all circumstances." This doesn't mean we pretend that difficulties don't exist, but rather that we trust God's goodness even in challenging times. Our gratitude becomes a prayer, a way of acknowledging that all good things come from the Lord.

I encourage you to continue this spirit of thanksgiving throughout the year. Look for opportunities to express gratitude to those around you, and remember to thank God for the countless ways He blesses us each day.

Blessings and peace,
Father Rejimon Devasia`,
        date: '2024-11-17',
        excerpt: 'This week, as we celebrated Thanksgiving, I was overwhelmed by the many blessings that surround our parish community...'
      },
      {
        id: '3',
        title: 'Walking in Faith Together',
        content: `Dear parish family,

One of the greatest joys of serving as your priest is witnessing the beautiful ways our community comes together in faith. Whether it's during our weekly Mass, in our ministry groups, or in times of celebration and sorrow, I see the Body of Christ alive and active among us.

Faith is not meant to be a solitary journey. We are called to walk together, supporting one another, sharing our joys and bearing each other's burdens. In our individualistic culture, this communal aspect of faith is both countercultural and deeply needed.

I think of the early Christian communities described in Acts, where believers "devoted themselves to the apostles' teaching and fellowship, to the breaking of bread and the prayers." This is what we strive to be – a community united in purpose, bound together by love, and committed to following Christ.

As we continue our journey together, I invite you to consider how you might deepen your involvement in our parish life. Whether through joining a ministry, participating in our educational programs, or simply reaching out to a fellow parishioner who might need encouragement, your presence matters.

With love and prayers,
Father Rejimon Devasia`,
        date: '2024-11-10',
        excerpt: 'One of the greatest joys of serving as your priest is witnessing the beautiful ways our community comes together in faith...'
      }
        ];
        
        setReflections(productionReflections);
        
        // Check for hash in URL to select specific reflection
        const hash = window.location.hash.substring(1);
        const targetReflection = hash ? productionReflections.find(r => r.id === hash) : null;
        
        if (targetReflection) {
          setSelectedReflection(targetReflection);
        } else {
          setSelectedReflection(productionReflections[0]);
        }
      }
    };

    fetchReflections();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-church-light py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-church-dark mb-4">
            Priest's Reflections
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Spiritual guidance and weekly reflections from Father Rejimon Devasia
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Reflections List */}
          <aside className="lg:col-span-1">
            <h2 className="text-xl font-semibold text-church-dark mb-4">Recent Reflections</h2>
            <div className="space-y-3">
              {reflections.map((reflection) => (
                <button
                  key={reflection.id}
                  onClick={() => setSelectedReflection(reflection)}
                  className={`w-full text-left p-4 rounded-lg border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue ${
                    selectedReflection?.id === reflection.id
                      ? 'bg-church-blue text-white border-church-blue'
                      : 'bg-white text-church-dark border-gray-200 hover:border-church-blue'
                  }`}
                >
                  <h3 className="font-semibold mb-2">{reflection.title}</h3>
                  <div className="flex items-center text-sm opacity-75 mb-2">
                    <CalendarDaysIcon className="h-4 w-4 mr-1" />
                    <time dateTime={reflection.date}>
                      {formatDate(reflection.date)}
                    </time>
                  </div>
                  <p className="text-sm opacity-90 line-clamp-2">
                    {reflection.excerpt}
                  </p>
                </button>
              ))}
            </div>
          </aside>

          {/* Selected Reflection */}
          <main className="lg:col-span-2">
            {selectedReflection && (
              <article className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="p-8">
                  <header className="mb-6">
                    <h2 className="text-2xl md:text-3xl font-serif font-bold text-church-dark mb-4">
                      {selectedReflection.title}
                    </h2>
                    <div className="flex items-center text-gray-500 mb-4">
                      <CalendarDaysIcon className="h-5 w-5 mr-2" />
                      <time dateTime={selectedReflection.date}>
                        {formatDate(selectedReflection.date)}
                      </time>
                    </div>
                  </header>

                  <div className="prose prose-lg max-w-none">
                    <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {selectedReflection.content}
                    </div>
                  </div>

                  <footer className="mt-8 pt-6 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        <p>Written by Father Rejimon Devasia</p>
                        <p>Parish Priest, All Saints Catholic Church</p>
                      </div>
                      <button 
                        onClick={() => {
                          const reflectionUrl = `${window.location.origin}/reflections#${selectedReflection.id}`;
                          
                          if (navigator.share) {
                            navigator.share({
                              title: selectedReflection.title,
                              text: selectedReflection.excerpt,
                              url: reflectionUrl
                            });
                          } else {
                            // Copy link to clipboard
                            navigator.clipboard.writeText(reflectionUrl).then(() => {
                              alert('Reflection link copied to clipboard!');
                            }).catch(() => {
                              // Final fallback - email share
                              const emailSubject = encodeURIComponent(`Reflection: ${selectedReflection.title}`);
                              const emailBody = encodeURIComponent(`I wanted to share this reflection with you:\n\n${selectedReflection.title}\n\n${selectedReflection.excerpt}\n\nRead the full reflection at: ${reflectionUrl}`);
                              window.location.href = `mailto:?subject=${emailSubject}&body=${emailBody}`;
                            });
                          }
                        }}
                        className="bg-church-gold hover:bg-yellow-600 text-white font-semibold py-2 px-4 rounded transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-gold focus:ring-offset-2"
                      >
                        Share Reflection
                      </button>
                    </div>
                  </footer>
                </div>
              </article>
            )}
          </main>
        </div>


      </div>
    </div>
  );
};

export default Reflections;